import { cs } from '../../lib/currency';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Shirt,
  Calendar,
  Loader2,
  ChevronRight,
  Package,
} from 'lucide-react';

const UniformCampaignsView = () => {
  const { _user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignOrders, setCampaignOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await api.get('/uniform/campaigns?active_only=true');
      setCampaigns(res.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load uniform campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignOrders = async (campaignId) => {
    setLoadingOrders(true);
    try {
      const res = await api.get(`/uniform/campaigns/${campaignId}/orders`);
      setCampaignOrders(res.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load campaign orders');
      setCampaignOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleSelectCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    fetchCampaignOrders(campaign.id);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Uniform Campaigns</h1>
          <p className="text-slate-600">View active uniform order campaigns</p>
        </div>

        {campaigns.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Shirt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No Active Campaigns</h3>
              <p className="text-sm text-slate-500 mt-1">
                There are no uniform campaigns available for your role at this time.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Campaigns List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shirt className="h-5 w-5 text-blue-600" />
                  Active Campaigns
                </CardTitle>
                <CardDescription>
                  {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} available
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {campaigns.map(campaign => (
                    <div
                      key={campaign.id}
                      onClick={() => handleSelectCampaign(campaign)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                        selectedCampaign?.id === campaign.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'hover:border-slate-300'
                      }`}
                      data-testid={`campaign-${campaign.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-900">{campaign.title}</h4>
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          </div>
                          {campaign.description && (
                            <p className="text-sm text-slate-600 mt-1">{campaign.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            {campaign.deadline && (
                              (() => {
                                const isPast = new Date(campaign.deadline + 'T23:59:59') < new Date();
                                return isPast 
                                  ? <Badge className="bg-red-100 text-red-700 text-xs">Orders Closed (Deadline: {new Date(campaign.deadline).toLocaleDateString()})</Badge>
                                  : <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      Deadline: {new Date(campaign.deadline).toLocaleDateString()}
                                    </span>;
                              })()
                            )}
                            {campaign.items && (
                              <span className="flex items-center gap-1">
                                <Package className="h-3 w-3" />
                                {campaign.items.length} item{campaign.items.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Campaign Details */}
            {selectedCampaign ? (
              <Card>
                <CardHeader>
                  <CardTitle>{selectedCampaign.title}</CardTitle>
                  <CardDescription>
                    {selectedCampaign.description || 'Campaign details'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Campaign Items */}
                    {selectedCampaign.items && selectedCampaign.items.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Available Items</h4>
                        <div className="space-y-2">
                          {selectedCampaign.items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <span className="text-sm font-medium">{item.label}</span>
                              <span className="text-sm text-slate-600">
                                {cs()}{item.price_min}{item.price_max > item.price_min ? ` - ${cs()}${item.price_max}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Orders Summary */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Orders Summary</h4>
                      {loadingOrders ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid grid-cols-3 gap-3">
                            <div className="p-3 bg-blue-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-blue-600">{campaignOrders.length}</p>
                              <p className="text-xs text-slate-600">Total Orders</p>
                            </div>
                            <div className="p-3 bg-green-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-green-600">
                                {campaignOrders.filter(o => o.status === 'submitted').length}
                              </p>
                              <p className="text-xs text-slate-600">Submitted</p>
                            </div>
                            <div className="p-3 bg-yellow-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-yellow-600">
                                {campaignOrders.filter(o => o.status === 'draft').length}
                              </p>
                              <p className="text-xs text-slate-600">Drafts</p>
                            </div>
                          </div>

                          {campaignOrders.length > 0 && (
                            <div className="max-h-[300px] overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-100 sticky top-0">
                                  <tr>
                                    <th className="text-left p-2 font-medium">Player</th>
                                    <th className="text-left p-2 font-medium">Status</th>
                                    <th className="text-right p-2 font-medium">Items</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {campaignOrders.map(order => (
                                    <tr key={order.id} className="hover:bg-slate-50">
                                      <td className="p-2">{order.player_name}</td>
                                      <td className="p-2">
                                        <Badge 
                                          className={
                                            order.status === 'submitted' 
                                              ? 'bg-green-100 text-green-700' 
                                              : 'bg-yellow-100 text-yellow-700'
                                          }
                                        >
                                          {order.status}
                                        </Badge>
                                      </td>
                                      <td className="p-2 text-right">{order.items?.length || 0}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700">Select a Campaign</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Click on a campaign to view details and orders
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UniformCampaignsView;
